import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTeamDashboard } from '../TeamDashboardContext';
import { apiGet, apiPatch, apiPost } from '../api';
import type { Budget } from '../types';
import './budget.css';

const SPEND_ROWS = [
  { initials: 'AK', name: 'Arjun K.', role: 'owner', rate: '₹500/hr', hrs: 12.5, spent: '₹6,250', color: 'var(--text)', barPct: 72, pct: '12.5%', spentColor: 'var(--text)' },
  { initials: 'SR', name: 'Sneha R.', role: 'lead', rate: '₹400/hr', hrs: 18, spent: '₹7,200', color: 'var(--text)', barPct: 84, pct: '14.4%', spentColor: 'var(--text)' },
  { initials: 'RM', name: 'Rahul M.', role: 'dev', rate: '₹500/hr', hrs: 8, spent: '₹4,000', color: 'var(--red)', barPct: 47, pct: '8%', spentColor: 'var(--red)' },
  { initials: 'PK', name: 'Priya K.', role: 'dev', rate: '₹500/hr', hrs: 4, spent: '₹2,000', color: 'var(--green)', barPct: 23, pct: '4%', spentColor: 'var(--green)' },
];

const TXN_HISTORY = [
  { type: 'in', icon: '↓', iconBg: 'var(--gdim)', iconBorder: 'var(--gborder)', iconColor: 'var(--green)', title: 'Budget deposit', sub: 'by Arjun Kumar', tag: '+₹25,000', tagClass: 'tag tg', time: '2 days ago' },
  { type: 'out', icon: '↑', iconBg: 'var(--rdim)', iconBorder: 'var(--rborder)', iconColor: 'var(--red)', title: 'Rahul Mehta', sub: '— 3 hrs on Auth bug fix', tag: '-₹1,500', tagClass: 'tag tr', time: '2h ago' },
  { type: 'out', icon: '↑', iconBg: 'var(--rdim)', iconBorder: 'var(--rborder)', iconColor: 'var(--red)', title: 'Sneha Rao', sub: '— 6 hrs on API refactor', tag: '-₹2,400', tagClass: 'tag tr', time: '5h ago' },
  { type: 'in', icon: '↓', iconBg: 'var(--gdim)', iconBorder: 'var(--gborder)', iconColor: 'var(--green)', title: 'Budget deposit', sub: 'by Arjun Kumar', tag: '+₹25,000', tagClass: 'tag tg', time: '7 days ago' },
];

function extractBudget(input: unknown): Budget | null {
  const payload = input && typeof input === 'object' && 'data' in (input as Record<string, unknown>)
    ? (input as { data?: unknown }).data
    : input;
  if (!payload || typeof payload !== 'object') return null;
  const b = payload as Record<string, unknown>;
  const total = Number(b.total ?? b.totalBudget ?? 0);
  const spent = Number(b.spent ?? 0);
  const remaining = Number(b.remaining ?? (total - spent));
  return {
    total: Number.isFinite(total) ? total : 0,
    spent: Number.isFinite(spent) ? spent : 0,
    remaining: Number.isFinite(remaining) ? remaining : 0,
  };
}

export default function BudgetPage() {
  const { showToast, isReal, budget, teamSlug, theme } = useTeamDashboard();
  const [mounted, setMounted] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [budgetMode, setBudgetMode] = useState<'add' | 'reduce'>('add');
  const [amount, setAmount] = useState('');
  const [localBudget, setLocalBudget] = useState<Budget | null>(null);

  useEffect(() => {
    const t1 = setTimeout(() => setMounted(true), 30);
    const t2 = setTimeout(() => setAnimated(true), 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    if (isReal) {
      setLocalBudget(budget);
    }
  }, [isReal, budget]);

  const activeBudget = isReal ? (localBudget ?? budget) : null;
  const totalDeposited = isReal ? Math.max(0, Number(activeBudget?.total ?? 0)) : 50000;
  const totalSpent = isReal ? Math.max(0, Number(activeBudget?.spent ?? 0)) : 17500;
  const remaining = isReal ? Math.max(0, Number(activeBudget?.remaining ?? (totalDeposited - totalSpent))) : 32500;
  const remainingPct = totalDeposited > 0 ? Math.max(0, Math.min(100, Math.round((remaining / totalDeposited) * 100))) : 0;
  const spentTodayLabel = isReal ? 'live' : '↓ ₹2,000 today';

  async function executeBudgetAction() {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      showToast('Budget', 'Please enter a valid amount', 'error');
      return;
    }

    if (!isReal) {
      showToast(budgetMode === 'add' ? 'Funds added' : 'Budget reduced', `₹${amt.toLocaleString()} ${budgetMode === 'add' ? 'deposited' : 'reduced'} successfully`, 'success');
      setAmount('');
      return;
    }

    const currentTotal = Math.max(0, Number(activeBudget?.total ?? 0));
    if (budgetMode === 'reduce' && amt > currentTotal) {
      showToast('Budget', 'Cannot reduce more than total budget', 'error');
      return;
    }

    const nextTotal = budgetMode === 'add' ? currentTotal + amt : Math.max(0, currentTotal - amt);

    const runPatch = async () => {
      await apiPatch(`/${teamSlug}/budget`, { totalBudget: nextTotal });
    };

    try {
      await runPatch();

      // Refresh budget from API for authoritative spent/remaining values.
      try {
        const refreshed = await apiGet<unknown>(`/${teamSlug}/budget`);
        const parsed = extractBudget(refreshed);
        if (parsed) {
          setLocalBudget(parsed);
        } else {
          const spentNow = Math.max(0, Number(activeBudget?.spent ?? 0));
          setLocalBudget({ total: nextTotal, spent: spentNow, remaining: Math.max(0, nextTotal - spentNow) });
        }
      } catch {
        const spentNow = Math.max(0, Number(activeBudget?.spent ?? 0));
        setLocalBudget({ total: nextTotal, spent: spentNow, remaining: Math.max(0, nextTotal - spentNow) });
      }

      showToast(
        budgetMode === 'add' ? 'Funds added' : 'Budget reduced',
        `₹${amt.toLocaleString()} ${budgetMode === 'add' ? 'deposited' : 'reduced'} successfully`,
        'success',
      );
      setAmount('');
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('403')) {
        showToast('Budget update failed', 'Only owner/lead/pm can change budget', 'error');
        return;
      }

      if (message.includes('404')) {
        try {
          await apiPost('/', { name: 'My Workspace', slug: teamSlug, plan: 'free', members: [] });
          await runPatch();
          const refreshed = await apiGet<unknown>(`/${teamSlug}/budget`);
          const parsed = extractBudget(refreshed);
          if (parsed) setLocalBudget(parsed);
          showToast(
            budgetMode === 'add' ? 'Funds added' : 'Budget reduced',
            `₹${amt.toLocaleString()} ${budgetMode === 'add' ? 'deposited' : 'reduced'} successfully`,
            'success',
          );
          setAmount('');
          return;
        } catch {
          // fall through to generic error
        }
      }

      showToast('Budget update failed', 'Could not update workspace budget', 'error');
    }
  }

  return (
    <div className="td-budget" data-theme={theme}>
      <div className="ptitle">Budget report</div>
      <div className="psub">Workspace: Scrudix Dev · Sprint 4</div>

      <div className="g3 mb">
        {[
          { eye: 'Total deposited', val: `₹${totalDeposited.toLocaleString()}`, change: isReal ? 'real mode' : '2 deposits', changeStyle: { color: 'var(--text3)' }, delay: '0s' },
          { eye: 'Total spent', val: `₹${totalSpent.toLocaleString()}`, change: spentTodayLabel, changeClass: isReal ? 'sc-change' : 'sc-change dn', delay: '.05s' },
          { eye: 'Remaining', val: `₹${remaining.toLocaleString()}`, valStyle: { color: 'var(--green)' }, change: `${remainingPct}% left`, changeClass: 'sc-change up', delay: '.1s' },
        ].map((s, i) => (
          <div key={i} className={`sc tilt fu${mounted ? ' in' : ''}`} style={{ transitionDelay: s.delay }}>
            <div className="sc-eye">{s.eye}</div>
            <div className="sc-val" style={s.valStyle}>{s.val}</div>
            <div className={s.changeClass || 'sc-change'} style={s.changeStyle}>{s.change}</div>
          </div>
        ))}
      </div>

      <div className="g2 mb">
        {/* Budget control */}
        <div className={`card fu${mounted ? ' in' : ''}`} style={{ transitionDelay: '.15s' }}>
          <div className="ch">
            <span className="ct">Budget control</span>
            <span style={{ fontSize: 9, fontFamily: 'var(--mono)', background: 'rgba(77,156,248,.12)', color: 'var(--blue)', border: '1px solid rgba(77,156,248,.25)', padding: '2px 8px', borderRadius: 4 }}>admin full access</span>
          </div>
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 14, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 3 }}>
              <button onClick={() => setBudgetMode('add')}
                style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 600, background: budgetMode === 'add' ? 'var(--green)' : 'transparent', color: budgetMode === 'add' ? '#000' : 'var(--text3)', transition: 'all .15s' }}>
                + Add Funds
              </button>
              <button onClick={() => setBudgetMode('reduce')}
                style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 600, background: budgetMode === 'reduce' ? 'var(--red)' : 'transparent', color: budgetMode === 'reduce' ? '#fff' : 'var(--text3)', transition: 'all .15s' }}>
                − Reduce Budget
              </button>
            </div>
            {budgetMode === 'add' ? (
              <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 10, padding: '8px 10px', background: 'var(--gdim)', border: '1px solid var(--gborder)', borderRadius: 7, display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ color: 'var(--green)', flexShrink: 0 }}><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                Deposit new funds into the workspace budget.
              </div>
            ) : (
              <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 10, padding: '8px 10px', background: 'var(--rdim)', border: '1px solid var(--rborder)', borderRadius: 7, display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ color: 'var(--red)', flexShrink: 0 }}><path d="M2 6h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                As admin you can reduce the budget. This cannot be undone.
              </div>
            )}
            <div className="fgroup">
              <label className="label">{budgetMode === 'add' ? 'Amount to add (₹)' : 'Amount to reduce (₹)'}</label>
              <input className="inp" type="number" placeholder="Enter amount" min={1} value={amount} onChange={e => setAmount(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              {[5000, 10000, 25000, 50000].map(v => (
                <button key={v} className="btn-s" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setAmount(String(v))}>₹{v.toLocaleString()}</button>
              ))}
            </div>
            <button className="btn-p" onClick={executeBudgetAction} style={{ width: '100%' }}>
              {budgetMode === 'add' ? 'Deposit funds' : 'Reduce budget'}
            </button>
          </div>
        </div>

        {/* Budget health */}
        <div className={`card fu${mounted ? ' in' : ''}`} style={{ transitionDelay: '.2s' }}>
          <div className="ch"><span className="ct">Budget health</span></div>
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 600, fontFamily: 'var(--mono)', letterSpacing: -1, color: 'var(--text)' }}>₹{remaining.toLocaleString()}</div>
                <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)', marginTop: 2 }}>of ₹{totalDeposited.toLocaleString()} · {remainingPct}% remaining</div>
              </div>
            </div>
            <div className="bbar" style={{ height: 5, marginBottom: 8 }}>
              <div className="bfill" style={{ width: animated ? `${remainingPct}%` : '0%', transition: 'width 1s ease' }} />
            </div>
            <div className="blbl"><span>₹0</span><span style={{ color: 'var(--green)' }}>{remainingPct}% left</span><span>₹{totalDeposited.toLocaleString()}</span></div>
            <div className="info-box info-green" style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 500, marginBottom: 2 }}>Budget is healthy</div>
              <div style={{ fontSize: 10, opacity: .8 }}>Alert triggers below 20% (₹{Math.round(totalDeposited * 0.2).toLocaleString()})</div>
            </div>
          </div>
        </div>
      </div>

      {/* Spend breakdown */}
      <div className={`card fu mb${mounted ? ' in' : ''}`} style={{ transitionDelay: '.25s' }}>
        <div className="ch"><span className="ct">Spend breakdown</span></div>
        {isReal ? (
          <div style={{ padding: 16, fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            Real mode active: no demo spend data shown.
          </div>
        ) : (
          <>
            <div className="qsh" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
              <span className="qsth">Member</span>
              <span className="qsth">Rate</span>
              <span className="qsth">Hours</span>
              <span className="qsth">Spent</span>
              <span className="qsth">Share</span>
            </div>
            {SPEND_ROWS.map(r => (
              <div key={r.initials} className="qsr" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
                <div className="qsm">
                  <div className="avr avsm">{r.initials}</div>
                  <div><div className="qsmn">{r.name}</div><div className="qsmr">{r.role}</div></div>
                </div>
                <span className="qstd">{r.rate}</span>
                <span className="qstd">{r.hrs}</span>
                <span className="qstd" style={{ color: r.spentColor }}>{r.spent}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ flex: 1, height: 2, background: 'var(--bg3)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: animated ? `${r.barPct}%` : '0%', height: '100%', background: r.color, borderRadius: 99, transition: 'width 1s ease' }} />
                  </div>
                  <span className="qstd">{r.pct}</span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Transaction history */}
      <div className={`card fu${mounted ? ' in' : ''}`} style={{ transitionDelay: '.3s' }}>
        <div className="ch"><span className="ct">Transaction history</span></div>
        {isReal ? (
          <div style={{ padding: 16, fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            Real mode active: no demo transactions shown.
          </div>
        ) : (
          TXN_HISTORY.map((t, i) => (
            <div key={i} className="ai">
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: t.iconBg, border: `1px solid ${t.iconBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: t.iconColor, fontSize: 12, fontWeight: 600 }}>{t.icon}</div>
              <div className="aib">
                <div className="ait"><strong>{t.title}</strong> {t.sub}</div>
                <div className="aif">
                  <span className={t.tagClass}>{t.tag}</span>
                  <span className="at">{t.time}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
